import Papa from "papaparse";

import dayjs from "dayjs-local";

export enum DateFormat {
  UnixTimestampInSeconds = "X",
  ISO8601WithTZ = "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]",
  ISO8601NoTZ = "YYYY-MM-DD HH:mm:ss.SSS",
}

export const establishDateFormat = (date: string): DateFormat | null => {
  const dateAsFloat = parseFloat(date);
  const is10DigitFloat =
    !isNaN(dateAsFloat) &&
    date.indexOf(":") === -1 &&
    dateAsFloat.toString().length === 10; // dates will still parse as floats, but ignore if date has : in
  const isValid = (parsedDate: dayjs.Dayjs) =>
    parsedDate.isValid() &&
    parsedDate.year() <= 2100 &&
    parsedDate.year() > 2010;
  if (is10DigitFloat) {
    const parsedDate = dayjs.unix(dateAsFloat);
    return isValid(parsedDate) ? DateFormat.UnixTimestampInSeconds : null;
  }
  for (const dateFormat of Object.values(DateFormat).slice(1)) {
    // skip unix
    const parsedDate = dayjs(date, dateFormat, true);
    if (isValid(parsedDate)) return dateFormat;
  }
  return null;
};

export const normalizeDateToUnixTimestampInMS = (
  date: string,
  format: DateFormat
): number => {
  switch (format) {
    case DateFormat.UnixTimestampInSeconds:
      return parseFloat(date) * 1000;
    case DateFormat.ISO8601NoTZ:
      return Date.parse(date.replace(/\s/, "T") + "Z");
    case DateFormat.ISO8601WithTZ:
      return Date.parse(date);
    default:
      throw new Error(`Date format ${format} not supported`);
  }
};

export const parseNumericCsv = (csvData: string) => {
  const parsedCsv = Papa.parse(csvData, {
    dynamicTyping: false,
    skipEmptyLines: true,
  });
  const rawCsvRows = parsedCsv.data.slice(1) as string[][];
  let error = "";
  let parsedRows: number[][] = [];
  if (rawCsvRows.length > 0) {
    const firstColumnInFirstDataRow = rawCsvRows[0][0];
    const dateFormat = establishDateFormat(firstColumnInFirstDataRow);
    if (dateFormat == null) {
      error = `Warning: first column should be a timestamp or date, but '${firstColumnInFirstDataRow}' doesn't look like one.`;
    }
    parsedRows = !dateFormat
      ? []
      : rawCsvRows.map((row) => [
          normalizeDateToUnixTimestampInMS(row[0], dateFormat!),
          ...row.slice(1).map(parseFloat),
        ]);
  }

  return {
    columnNames: (parsedCsv.data?.[0] as string[]) || [],
    parsedRows,
    rawRows: rawCsvRows,
    error,
  };
};
